import { useEffect } from "react";

/**
 * Per-route document metadata: title, description, canonical, Open Graph, Twitter
 * card and optional JSON-LD.
 *
 * A CRA single-page app ships one static index.html, so without this every route
 * reports the same title and description — which collapses the whole site into a
 * single result for search engines and gives AI crawlers nothing to distinguish
 * pages by. This sets them per route at runtime.
 *
 * Known limitation: crawlers that don't execute JavaScript still see the static
 * shell. Googlebot renders JS and will pick this up; several AI crawlers do not.
 * The durable fix is server-side rendering or prerendering at the edge — the
 * backend's /api/seo/* endpoints exist so a prerenderer has clean data to work
 * from when that's added.
 */
const SITE = "Co Stock";
const ORIGIN = typeof window !== "undefined" ? window.location.origin : "";

function upsertMeta(selector, attrs) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    Object.entries(attrs).forEach(([k, v]) => k !== "content" && el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  el.setAttribute("content", attrs.content || "");
}

export default function useSeo({ title, description, image, noindex, jsonLd, path } = {}) {
  useEffect(() => {
    const fullTitle = title ? `${title} · ${SITE}` : SITE;
    document.title = fullTitle;

    if (description) {
      upsertMeta('meta[name="description"]', { name: "description", content: description });
    }

    // Private, per-user pages must not be indexed even if a URL leaks.
    upsertMeta('meta[name="robots"]', {
      name: "robots",
      content: noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large",
    });

    const url = ORIGIN + (path || window.location.pathname);
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);

    upsertMeta('meta[property="og:title"]', { property: "og:title", content: fullTitle });
    upsertMeta('meta[property="og:description"]', { property: "og:description", content: description || "" });
    upsertMeta('meta[property="og:type"]', { property: "og:type", content: "website" });
    upsertMeta('meta[property="og:url"]', { property: "og:url", content: url });
    upsertMeta('meta[property="og:site_name"]', { property: "og:site_name", content: SITE });
    if (image) upsertMeta('meta[property="og:image"]', { property: "og:image", content: image });
    upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: image ? "summary_large_image" : "summary" });
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: fullTitle });
    upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: description || "" });

    // Structured data is re-created per route and removed on unmount, so stale
    // entities from a previous page can never linger in the DOM.
    let script;
    if (jsonLd) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-seo", "route");
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }
    return () => { if (script) script.remove(); };
  }, [title, description, image, noindex, jsonLd, path]);
}
